<template>
  <div id="staff">
    <Breadcrumbs
      :crumbs="[
        { name: mainStore.globals.info.school_name, link: `${mainStore.pathPrefix}/` },
        { name: mainStore.sectionName, link: `${mainStore.pathPrefix}/staff` },
      ]"
    />
    <div class="page-container">
      <div class="filters">
        <div class="search">
          <label
            for="search-input"
            class="sr-only"
            >{{ $t('pages.shared.search') }}</label
          >
          <text-input
            id="search-input"
            type="search"
            name="search"
            :model-value="search"
            :placeholder="$t('pages.shared.searchPlaceholder')"
            :disabled="!!memberName"
            @update:model-value="searchEvent"
          />
          <img
            src="../../assets/search.svg"
            class="search-icon"
            alt=""
          />
          <div
            v-if="memberName"
            class="member-tag"
            @click="removeMemberIdSearch"
          >
            <p>{{ memberName }}</p>
            <img
              src="../../assets/cms-close-icon.svg"
              class="close-icon"
              :alt="$t('pages.shared.close')"
            />
          </div>
        </div>
        <div
          class="sr-only"
          aria-live="polite"
        >
          {{ searchResultsMsg }}
        </div>
        <Filters
          v-if="!!filters.length"
          :filters="filters"
          :placeholder="$t('pages.staff.departmentsPlaceholder')"
          name="departments"
          @on-filter="onFilterChange"
        />
      </div>
      <LoadingIndicator v-if="loading" />
      <div
        v-for="(column, idx) in rows"
        v-else
        :key="idx"
        class="page-container-item"
      >
        <StaffCard
          v-for="member in column"
          :key="member.id"
          :member="member"
          :email="staffEmails"
          @open-contact-modal="handleOpenContactModal"
        />
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
    <StaffContactModal
      v-if="memberToContact"
      :member="memberToContact"
      @close-modal="handleOpenContactModal(null)"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { useMainStore } from '../../stores/mainStore';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Filters from '../../components/global/FiltersDropdown.vue';
import Pagination from '../../components/global/Pagination.vue';
import StaffCard from '../../components/pages/staff/StaffCard';
import TextInput from '../../components/global/Forms/TextInput.vue';
import StaffContactModal from '../../components/pages/staff/StaffContactModal/StaffContactModal';
import { setupAndFetchStaff } from '../../helpers/section/sections.helper';

defineOptions({ name: 'Staff' });

const mainStore = useMainStore();
const route = useRoute();
const router = useRouter();
const { $axios } = useNuxtApp();

const loading = ref(false);
const staff = ref([]);
const filters = ref([]);
const currentPage = ref(0);
const totalPages = ref(0);
const totalEntries = ref(0);
const memberName = ref(null);
const memberToContact = ref(null);
const search = ref('');

const { data: initialData } = await useAsyncData('staff', () => setupAndFetchStaff(mainStore, route.query, $axios));

if (initialData.value) {
  staff.value = initialData.value.staff ?? [];
  filters.value = initialData.value.filters ?? [];
  totalPages.value = initialData.value.totalPages ?? 0;
  currentPage.value = initialData.value.currentPage ?? 0;
  totalEntries.value = initialData.value.total_entries ?? 0;
  if (initialData.value.memberName != null) {
    memberName.value = initialData.value.memberName;
  }
  if (initialData.value.search != null) {
    search.value = initialData.value.search;
  }
}
const sectionName = computed(() => mainStore.sectionName);

useHead({
  title: sectionName.value,
});

const rows = computed(() => chunk(staff.value, 2));
const staffEmails = computed(() => mainStore.settings?.staffEmails || 'display_emails');

const searchResultsMsg = computed(() => {
  const { $t } = useNuxtApp();
  if (memberName.value) {
    return `${$t('pages.staff.memberFound', { amount: 1 })} ${$t('pages.staff.removeTag')}`;
  }
  if (search.value) {
    return $t('pages.staff.memberFound', { amount: totalEntries.value });
  }
  return $t('pages.staff.filterByName');
});

watch(
  () => route.query.page_no,
  (newVal, oldVal) => {
    if (import.meta.client && oldVal !== newVal) {
      window.scrollTo(0, 0);
    }
  },
);

onBeforeRouteUpdate((to, from, next) => {
  if (
    import.meta.client &&
    (to.query.page_no !== from.query.page_no ||
      !isEqual(to.query.filter_ids, from.query.filter_ids) ||
      to.query.search !== from.query.search ||
      to.query.id !== from.query.id)
  ) {
    fetchStaff(to.query);
  }
  next();
});

async function fetchStaff(query) {
  loading.value = true;
  totalPages.value = 0;
  const result = await setupAndFetchStaff(mainStore, query, $axios);
  staff.value = result.staff ?? [];
  filters.value = result.filters ?? [];
  totalPages.value = result.totalPages ?? 0;
  currentPage.value = result.currentPage ?? 0;
  totalEntries.value = result.total_entries ?? 0;
  if (result.memberName != null) {
    memberName.value = result.memberName;
  }
  if (result.search != null) {
    search.value = result.search;
  }
  loading.value = false;
}

function onFilterChange(filterArr) {
  const filter_ids = filterArr.length > 0 ? filterArr.join(',') : undefined;
  router.replace({
    query: {
      ...route.query,
      page_no: undefined,
      filter_ids,
    },
  });
}

function handleOpenContactModal(member) {
  memberToContact.value = member;
}

const searchDebounce = debounce(() => searchCall(), 500);

function searchEvent(text) {
  search.value = text;
  searchDebounce();
}

function searchCall() {
  navigateTo({
    query: {
      ...route.query,
      page_no: undefined,
      search: search.value === '' ? undefined : search.value,
    },
  });
}

function removeMemberIdSearch() {
  memberName.value = null;
  navigateTo({ query: { id: undefined } });
}
</script>

<style scoped lang="scss">
#staff {
  .page-container-item {
    max-width: none;
    margin: 0 70px;
    padding: 0;
    @media screen and (max-width: 1000px) {
      border-bottom: none;
      margin: 0;
    }
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
    .search {
      position: relative;
      margin-top: 10px;
      margin-left: 5px;
      .search-icon {
        position: absolute;
        top: 14px;
        right: 10px;
      }
      .member-tag {
        background-color: #f3f3f3;
        border-radius: 16px;
        position: absolute;
        padding: 2px 7px;
        font-size: 12px;
        top: 10px;
        left: 8px;
        display: flex;
        cursor: pointer;
        .close-icon {
          margin-left: 10px;
          max-height: 8px;
          max-width: 8px;
          margin-top: 4px;
        }
      }
    }
  }
}
</style>
